import React from 'react';
import './Product.css';

const features = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
        <rect x="9" y="3" width="6" height="4" rx="2"/>
        <line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>
      </svg>
    ),
    title: 'Smart Digital Menus',
    desc: 'Create beautiful, interactive digital menus with QR code access. Update items, prices and availability in real-time — no reprinting needed.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
        <line x1="16" y1="7" x2="16" y2="3"/><line x1="8" y1="7" x2="8" y2="3"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    title: 'AI Inventory Control',
    desc: 'Machine-learning algorithms predict stock needs, flag low inventory, and automate reorder triggers — cutting waste and stockouts simultaneously.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: 'Sales Analytics',
    desc: 'Deep-dive into order patterns, peak hours, top-performing items and revenue trends — all in one unified dashboard built for restaurant owners.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
      </svg>
    ),
    title: 'AI-Powered Suggestions',
    desc: 'The AI engine recommends upsells, combo suggestions and seasonal specials to your customers — increasing average order value automatically.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/>
      </svg>
    ),
    title: 'Table-Side Ordering',
    desc: 'Guests scan, browse and place orders directly from their phones. Orders hit the kitchen instantly — no waitstaff bottleneck, faster table turns.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Multi-Branch Control',
    desc: 'Manage menus, inventory and staff access across unlimited locations from a single command center — built to scale with your restaurant group.',
  },
];

const stats = [
  { value: '500+', label: 'Restaurants Onboarded' },
  { value: '2M+',  label: 'Orders Processed' },
  { value: '35%',  label: 'Avg. Waste Reduction' },
  { value: '4.9★', label: 'App Store Rating' },
];

export default function Product() {
  return (
    <section className="product section" id="product">
      <div className="grid-bg" />
      <div className="product-orb product-orb--cyan" />
      <div className="product-orb product-orb--purple" />

      <div className="container">
        {/* Header */}
        <div className="product-header">
          <div className="product-header-left">
            <span className="section-tag">Our Flagship Product</span>
            <h2 className="section-title">
              MEET <span className="gradient-text">MENUCARD.AI</span>
            </h2>
            <p className="product-intro">
              Built by BlueClan, MenuCard.ai is an AI-powered digital menu and inventory
              management platform for modern restaurants. From QR menus to predictive
              stock control — one system runs your entire operation.
            </p>
            <div className="product-actions">
              <a
                href="https://www.menucard.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <span>Visit MenuCard.ai</span>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                  <path d="M7 3H3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9"/>
                  <path d="M13 3l-6 6M13 3H9M13 3v4"/>
                </svg>
              </a>
              <a href="#contact" className="btn-outline">Build Yours</a>
            </div>
          </div>

          {/* Product preview card */}
          <div className="product-preview">
            <div className="preview-card glass-card">
              <div className="preview-bar">
                <span className="preview-dot r" /><span className="preview-dot y" /><span className="preview-dot g" />
                <span className="preview-url">menucard.ai</span>
                <span className="preview-secure">
                  <svg viewBox="0 0 12 12" fill="currentColor" width="10" height="10"><path d="M6 1L2 3v3c0 2.5 1.8 4.7 4 5.4C8.2 10.7 10 8.5 10 6V3L6 1z"/></svg>
                  HTTPS
                </span>
              </div>

              {/* Mock UI */}
              <div className="preview-body">
                <div className="mock-sidebar">
                  <div className="mock-logo-bar">
                    <div className="mock-logo-dot" />
                    <div className="mock-logo-text" />
                  </div>
                  {[...Array(5)].map((_, i) => (
                    <div className="mock-nav-item" key={i} style={{ opacity: i === 0 ? 1 : 0.4 }}>
                      <div className="mock-nav-icon" />
                      <div className="mock-nav-label" style={{ width: `${60 + i * 8}%` }} />
                    </div>
                  ))}
                </div>
                <div className="mock-main">
                  <div className="mock-topbar">
                    <div className="mock-title-block">
                      <div className="mock-h" style={{ width: '60%' }} />
                      <div className="mock-p" style={{ width: '40%' }} />
                    </div>
                    <div className="mock-btn-block">
                      <div className="mock-btn" />
                    </div>
                  </div>
                  <div className="mock-stats-row">
                    {[...Array(3)].map((_, i) => (
                      <div className="mock-stat-card" key={i}>
                        <div className="mock-stat-val" style={{ width: '50%', background: i === 0 ? 'var(--cyan)' : i === 1 ? 'var(--purple)' : 'var(--gold)' }} />
                        <div className="mock-stat-lbl" style={{ width: '70%' }} />
                      </div>
                    ))}
                  </div>
                  <div className="mock-table">
                    <div className="mock-table-head">
                      {[...Array(4)].map((_, i) => <div className="mock-th" key={i} />)}
                    </div>
                    {[...Array(4)].map((_, r) => (
                      <div className="mock-row" key={r}>
                        {[...Array(4)].map((_, c) => <div className="mock-td" key={c} style={{ width: c === 0 ? '30%' : c === 3 ? '15%' : '20%' }} />)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating status */}
              <div className="preview-badge">
                <span className="pb-dot" />
                AI Engine Active
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="product-stats glass-card">
          {stats.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className="pstat">
                <span className="pstat-val">{s.value}</span>
                <span className="pstat-lbl">{s.label}</span>
              </div>
              {i < stats.length - 1 && <div className="pstat-divider" />}
            </React.Fragment>
          ))}
        </div>

        {/* Features grid */}
        <div className="product-features-header">
          <span className="section-tag">Capabilities</span>
          <h3 className="section-title" style={{ fontSize: 'clamp(22px, 3vw, 36px)' }}>
            EVERYTHING YOUR RESTAURANT <span className="gradient-text">NEEDS</span>
          </h3>
        </div>

        <div className="features-grid">
          {features.map(f => (
            <div className="feat-card glass-card" key={f.title}>
              <div className="feat-icon">{f.icon}</div>
              <h4 className="feat-title">{f.title}</h4>
              <p className="feat-desc">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA banner */}
        <div className="product-cta-banner glass-card">
          <div className="cta-banner-left">
            <span className="cta-tag">// Built by BlueClan</span>
            <h3 className="cta-title">Want a Product Like This?</h3>
            <p className="cta-desc">
              We build AI-powered SaaS products from scratch — product design,
              engineering, and launch. Tell us your idea.
            </p>
          </div>
          <div className="cta-banner-right">
            <a href="#contact" className="btn-primary"><span>Start Your Product</span></a>
            <a
              href="https://www.menucard.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
            >
              See MenuCard Live
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
